import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Trophy, TrendingUp, ArrowUp, ArrowDown, Minus, Users as UsersIcon, Info } from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';

const PRESETS = [
  { id: '7d',   label: 'Last 7 days',   days: 7 },
  { id: '30d',  label: 'Last 30 days',  days: 30 },
  { id: '90d',  label: 'Last 90 days',  days: 90 },
  { id: 'ytd',  label: 'Year to date',  days: null },
  { id: 'all',  label: 'All time',      days: null },
];

function rangeFor(preset) {
  const to = new Date();
  if (preset === 'ytd') {
    return { from: new Date(to.getFullYear(), 0, 1), to };
  }
  if (preset === 'all') {
    // Wide enough to predate any CRM record. Server-side aggregates use
    // simple $gte filters, so this is effectively "no lower bound".
    return { from: new Date('2000-01-01'), to };
  }
  const days = PRESETS.find((p) => p.id === preset)?.days || 30;
  return { from: new Date(to.getTime() - days * 86400000), to };
}

const COLUMNS = [
  { key: 'name',         label: 'User',         align: 'left',  sortable: true,
    hint: 'All users in this organization. Users with no activity in the range still appear with zeros.' },
  { key: 'newLeads',     label: 'New leads',    align: 'right', sortable: true,
    hint: 'Leads CREATED inside the selected window and assigned to this user (or unassigned). Range-based.' },
  { key: 'won',          label: 'Won',          align: 'right', sortable: true,
    hint: 'Leads moved into a Won stage inside the window. Counts each transition once. Range-based.' },
  { key: 'lost',         label: 'Lost',         align: 'right', sortable: true,
    hint: 'Leads moved into a Lost stage inside the window. Range-based.' },
  { key: 'winRate',      label: 'Win rate',     align: 'right', sortable: true, format: (v) => `${v}%`,
    hint: 'Won ÷ (Won + Lost) for this window. Open leads are not counted.' },
  { key: 'revenue',      label: 'Revenue',      align: 'right', sortable: true, format: (v) => formatCurrency(v),
    hint: 'Realized revenue: sum of estimatedValue for deals WON in the window. Past-looking, locked-in money.' },
  { key: 'avgDealSize',  label: 'Avg deal',     align: 'right', sortable: true, format: (v) => formatCurrency(v),
    hint: 'Revenue ÷ Won. Average size of the deals this rep closed in the window.' },
  { key: 'avgCycleDays', label: 'Avg cycle',    align: 'right', sortable: true, format: (v) => v ? `${v}d` : '—',
    hint: 'Average days between lead creation and Won transition, for deals won in the window.' },
  { key: 'activeLeads',  label: 'Active',       align: 'right', sortable: true,
    hint: 'OPEN leads currently assigned to this user (any non-closed stage). Snapshot, ignores the window.' },
  { key: 'pipelineValue',label: 'Pipeline $',   align: 'right', sortable: true, format: (v) => formatCurrency(v),
    hint: 'Open potential: sum of estimatedValue across the rep\'s currently active leads. Forward-looking; not yet won.' },
];

const SortIcon = ({ active, direction }) => {
  if (!active) return <Minus size={11} className="text-gray-300" />;
  return direction === 'asc'
    ? <ArrowUp size={11} className="text-primary-600" />
    : <ArrowDown size={11} className="text-primary-600" />;
};

const UserPerformanceReport = () => {
  const [preset, setPreset] = useState('90d');
  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');

  const { from, to } = rangeFor(preset);

  const { data, isLoading } = useQuery(
    ['reports', 'users', preset],
    () => api.get('/reports/users', {
      params: { from: from.toISOString(), to: to.toISOString() }
    }).then((r) => r.data),
    { keepPreviousData: true }
  );

  const rows = data?.users || [];
  const totals = data?.totals || {};

  const sortedRows = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = typeof av === 'string'
        ? String(av).localeCompare(String(bv))
        : (av || 0) - (bv || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const topPerformer = sortedRows.find((r) => r.userId && r.won > 0);
  const maxRevenue = Math.max(...rows.map((r) => r.revenue), 1);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <UsersIcon size={28} /> Per-User Performance
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Compare reps on leads worked, conversions, revenue, and cycle length.
          </p>
        </div>
        <div className="flex flex-wrap gap-1 bg-white border border-gray-200 rounded-lg p-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                preset === p.id
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center text-gray-400 text-sm">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
          <UsersIcon size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No users found for this organization.</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <SummaryCard label="Total Won" value={totals.won || 0} />
            <SummaryCard label="Total Lost" value={totals.lost || 0} />
            <SummaryCard label="Win Rate" value={`${totals.winRate || 0}%`} />
            <SummaryCard label="Total Revenue" value={formatCurrency(totals.revenue || 0)} highlight />
          </div>

          {topPerformer && (
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl flex items-center gap-3">
              <Trophy size={24} className="text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm">
                  <strong>{topPerformer.name}</strong> is leading with{' '}
                  <strong>{formatCurrency(topPerformer.revenue)}</strong> revenue across{' '}
                  <strong>{topPerformer.won}</strong> won deal{topPerformer.won === 1 ? '' : 's'}.
                </p>
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'} text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.sortable ? (
                            <button
                              onClick={() => handleSort(col.key)}
                              className="inline-flex items-center gap-1 hover:text-gray-900"
                            >
                              {col.label}
                              <SortIcon active={sortKey === col.key} direction={sortDir} />
                            </button>
                          ) : (
                            col.label
                          )}
                          {col.hint && <HintTooltip text={col.hint} align={col.align} />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => (
                    <tr key={row.userId || 'unassigned'} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                      {COLUMNS.map((col) => {
                        const v = row[col.key];
                        const display = col.format ? col.format(v) : v;
                        if (col.key === 'name') {
                          return (
                            <td key={col.key} className="px-4 py-3">
                              <div className="font-medium text-gray-900">{row.name}</div>
                              {row.email && <div className="text-[11px] text-gray-500">{row.email}</div>}
                              {row.role && row.role !== 'user' && (
                                <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wider text-gray-400">
                                  {row.role}
                                </span>
                              )}
                            </td>
                          );
                        }
                        if (col.key === 'revenue') {
                          const pct = maxRevenue > 0 ? (row.revenue / maxRevenue) * 100 : 0;
                          return (
                            <td key={col.key} className="px-4 py-3 text-right">
                              <div className="font-semibold text-gray-900">{display}</div>
                              <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary-500 rounded-full"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </td>
                          );
                        }
                        return (
                          <td
                            key={col.key}
                            className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.key === 'winRate' && v >= 50 ? 'text-green-700 font-semibold' : 'text-gray-700'}`}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            "New leads", "Won", "Lost", "Revenue", "Avg cycle" reflect activity within the selected window. "Active" and "Pipeline $" are current snapshots. Users with no activity in the range are still shown.
          </p>
        </>
      )}
    </div>
  );
};

// Custom hover tooltip — native `title` is delayed and unreliable on small
// SVG icons across browsers, so we render our own popover positioned over
// the column header. `group` on the wrapper triggers visibility.
const HintTooltip = ({ text, align = 'left' }) => (
  <span className="relative inline-flex group">
    <span className="cursor-help text-gray-300 hover:text-gray-500 inline-flex">
      <Info size={11} />
    </span>
    <span
      className={`pointer-events-none absolute z-30 ${align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'} top-full mt-1.5 w-80 max-w-[20rem] whitespace-normal rounded-md bg-gray-900 px-3 py-2 text-[11px] leading-snug font-normal normal-case tracking-normal text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150`}
    >
      {text}
    </span>
  </span>
);

const SummaryCard = ({ label, value, highlight }) => (
  <div className={`p-4 rounded-xl border ${highlight ? 'bg-primary-50 border-primary-200' : 'bg-white border-gray-100'}`}>
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className={`mt-1 text-2xl font-bold ${highlight ? 'text-primary-700' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default UserPerformanceReport;
